/**
 * Canonical forms of the two identifiers a clinic looks a patient up by, and the
 * rules for what may be stored as one.
 *
 * Pure string work with no SQL, crypto, DB or React knowledge, because it has to
 * run in four places that must agree exactly:
 *
 *   - the backend write path, which fills the `*_normalized` columns
 *   - the backend search path, which normalizes the query the same way
 *   - the frontend forms, which must reject a bad NIC BEFORE submitting
 *
 * If any two of those disagreed, the lookup would simply stop finding rows — no
 * error, no failing request, just a patient who cannot be found. Hence one
 * definition in a shared package rather than a copy per stack; `@synapse/item-types`
 * is the precedent.
 *
 * The frontend reaches this through the vite/tsconfig `@synapse/*` aliases, so it
 * must stay dependency-free and runtime-agnostic.
 */

/**
 * The two shapes a Sri Lankan NIC comes in, checked against the normalized form.
 *
 *   old (pre-2016) — 9 digits then V or X:  '953120456V'
 *   new            — 12 digits, no letter:  '199531204560'
 *
 * In both, the leading digits are a birth year (2- or 4-digit) followed by a
 * day-of-year, which is why the lengths are fixed rather than a range. Only the
 * shape is checked: the day-of-year and check digit are left alone deliberately,
 * because rejecting a card a patient is physically holding is a worse failure at
 * the registration desk than storing a typo.
 */
const NIC_PATTERN = /^(?:\d{9}[VX]|\d{12})$/;

/**
 * NIC as a search key: everything that is not a letter or digit is dropped, and
 * case is folded.
 *
 *   '953 120 456v'  → '953120456V'
 *   '95.3120456/V'  → '953120456V'
 *   '199531204560'  → '199531204560'
 *
 * The character class is deliberately a whitelist rather than a list of known
 * separators. NIC is matched by EQUALITY, so a separator this function fails to
 * fold is not a near-miss — it is a patient who can never be found again, and
 * nothing would report it. `isNationalIdWellFormed` keeps such values out of the
 * column going forward; this stays permissive for the rows that predate it and
 * for whatever staff type into the search box.
 *
 * Old (9 digits + V/X) and new (12 digit) NICs are deliberately NOT treated as
 * the same person even when they are — converting between them needs a century
 * assumption and a serial re-pad, and a lookup silently unifying two identifiers
 * is a worse failure than one that makes staff type the number they were given.
 */
export function normalizeNationalId(raw: string): string {
  return raw.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

/**
 * Does this parse as a Sri Lankan NIC once normalized?
 *
 * Enforced on every write so the stored column holds one canonical spelling per
 * card. That is what makes the equality lookup sound: if arbitrary text could
 * land there, two records of the same person could differ by a punctuation mark
 * and neither would find the other.
 *
 * The cost is that a passport or a foreign ID no longer fits this field.
 */
export function isNationalIdWellFormed(raw: string): boolean {
  return NIC_PATTERN.test(normalizeNationalId(raw));
}

/**
 * Phone as a search key: the national significant number, digits only.
 *
 *   '+94 77 123 4567' → '771234567'
 *   '0094771234567'   → '771234567'
 *   '077-123-4567'    → '771234567'
 *   '9999'            → '9999'        (partial query — left alone)
 *   'charlie'         → ''            (caller must skip an empty term)
 *
 * Stripping the country code and the trunk '0' is what makes those first three
 * match each other; storing them verbatim is why they did not match before 0016.
 *
 * An empty result is meaningful: it means the input carried no digits at all.
 * A caller building a LIKE pattern MUST skip the term rather than search for
 * '%%', which matches every row in the clinic.
 */
export function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");

  // International prefix dialled the long way round.
  const withoutIddPrefix = digits.startsWith("00") ? digits.slice(2) : digits;

  // Sri Lanka country code, but only at the exact length a real number has —
  // otherwise a partial query like '94123' would lose its first two digits.
  if (withoutIddPrefix.startsWith("94") && withoutIddPrefix.length === 11) {
    return withoutIddPrefix.slice(2);
  }

  // Trunk prefix. Guarded on length so a lone '0' does not normalize to ''.
  if (withoutIddPrefix.startsWith("0") && withoutIddPrefix.length > 1) {
    return withoutIddPrefix.slice(1);
  }

  return withoutIddPrefix;
}

/**
 * Fewest digits accepted as a stored contact number. Sri Lankan numbers carry
 * nine significant digits; the floor sits below that so a foreign number still
 * fits, but high enough that placeholder text ('n/a', '-', 'none') cannot
 * masquerade as a phone.
 */
const MIN_PHONE_DIGITS = 7;

/**
 * Is this storable as a phone number?
 *
 * Enforced on write for one specific reason: a value that normalizes to nothing
 * cannot have a search key derived from it, which would leave the row matching
 * `phone IS NOT NULL AND phone_normalized IS NULL` forever — a row that exists but
 * cannot be found by the number on it, with nothing to report the fact.
 *
 * This validates the *stored* value, not a search query. A user typing a partial
 * number into the search box is fine; a partial number in the column is a contact
 * nobody can ring.
 */
export function isPhoneWellFormed(raw: string): boolean {
  return raw.replace(/\D/g, "").length >= MIN_PHONE_DIGITS;
}

/**
 * Shortest and longest an MSISDN may be. E.164 caps a subscriber number at 15
 * digits including the country code; the floor is the shortest national plan in
 * use anywhere plus a one-digit country code.
 */
const MIN_MSISDN_DIGITS = 8;
const MAX_MSISDN_DIGITS = 15;

/**
 * Digits in a national significant number under the HOME country's plan — Sri
 * Lanka's, which is nine (`77 123 4567`, `11 234 5678`).
 *
 * This constant is why `defaultCountryCode` is a parameter for the country CODE
 * only and not a claim of multi-country support: the rules below also encode a
 * nine-digit national plan, so passing `'61'` produces Australian-looking
 * numbers built to Sri Lankan lengths. Deploying elsewhere needs this value and
 * the trunk rules revisited, not just the env var changed.
 *
 * It earns its place by rejecting the two shapes that would otherwise dial a
 * WRONG number rather than none: a country code followed by a retained trunk
 * zero (`+94 077 …` → 12 digits), and trailing junk such as an extension
 * (`+94 77 123 4567 ext 2` → 12 digits).
 */
const HOME_NATIONAL_DIGITS = 9;

/**
 * The HOME country's mobile prefixes — the first two digits of a Sri Lankan
 * national significant number that can receive an SMS.
 *
 * Two things follow from pinning national-format numbers to these, and both are
 * the point:
 *
 *  1. **A number that cannot receive an SMS is not an SMS destination.** A
 *     Colombo landline (`011 234 5678`) previously became `94112345678`, which
 *     the gateway refuses — burning a `failed` row and an operator's attention
 *     on a number that was never textable.
 *  2. **It narrows the window in which a FOREIGN number written in its own
 *     national trunk format is claimed as Sri Lankan.** That is the serious
 *     one: the results SMS carries the patient's name and their portal token —
 *     an unexpiring, unrotatable bearer credential to their lab history — so a
 *     misread number sends PHI to a stranger, silently and with no audit trail
 *     (portal reads are deliberately unlogged). `0612345678`, `0412345678`,
 *     `0812345678` and `0123456789` are all rejected now instead of being
 *     re-badged `94…`.
 *
 * It does NOT close that window completely, and pretending otherwise would be
 * worse than saying so: a French `07 12 34 56 78` has exactly the shape of a Sri
 * Lankan mobile, and no parser can tell them apart. That case is undecidable
 * here and has to be handled at data entry — a foreign number must be stored
 * with its country code. Only rules 3 and 4 are affected; a value that declares
 * its own country code (`+33 …`) is never touched by this list.
 *
 * 73 and 79 are deliberately absent: unassigned by the TRC.
 */
const HOME_MOBILE_PREFIXES = ["70", "71", "72", "74", "75", "76", "77", "78"];

function isHomeMobile(nationalNumber: string): boolean {
  return HOME_MOBILE_PREFIXES.some((prefix) => nationalNumber.startsWith(prefix));
}

/** ASCII digits only. Unicode digits are a red flag, not a formatting quirk. */
const NON_ASCII_DIGIT = /[^\x00-\x7F]/;

/**
 * The DIALABLE form: MSISDN, country code included, no leading '+'.
 * `null` when the stored value cannot be turned into one without guessing.
 *
 * This is the third canonical form of a phone number in this file, and the only
 * one that has to be correct for a message to leave the building. It is
 * deliberately derived from the RAW `patients.phone`, NEVER from
 * `phone_normalized`:
 *
 *   normalizePhone('+65 9123 4567') → '6591234567'
 *   normalizePhone('071 234 5678')  → '712345678'
 *
 * — the country code is gone by then, and `isPhoneWellFormed` accepts anything
 * with seven digits, so a foreign number and a Sri Lankan one are
 * indistinguishable at that point. Prefixing '94' onto the wrong one texts a
 * stranger, and the message names the patient. That is the failure this
 * function exists to prevent, which is why it re-parses from source rather than
 * reusing the search key sitting right there.
 *
 * `null` is a first-class answer, not an error case. An eight-digit number with
 * no country code is storable (`isPhoneWellFormed` accepts it), undialable
 * without inventing a country, and the only honest thing to do with it is
 * decline to text it. Callers skip the send; they do not substitute a default.
 *
 * `defaultCountryCode` is applied ONLY to values that already look like
 * national-format numbers of that country (rules 3 and 4). It is not a fallback
 * sprayed over anything that fails to parse — and see HOME_NATIONAL_DIGITS for
 * why it is a country CODE parameter rather than multi-country support.
 */
export function toMsisdn(raw: string, defaultCountryCode: string): string | null {
  const trimmed = raw.trim();

  // Unicode digits are refused outright rather than stripped.
  //
  // `\D` is ASCII-only without the `u` flag, so a full-width '４' or an Eastern
  // Arabic '٤' is DELETED by the strip below rather than kept — silently
  // shortening the number and shifting every digit left. '+9４771234567' would
  // become '9771234567', which is a real Nepali number. A value containing them
  // is a paste from somewhere unexpected, and the only safe reading is "we do
  // not know what this is".
  if (NON_ASCII_DIGIT.test(trimmed)) return null;

  const digits = trimmed.replace(/\D/g, "");

  // '+' and '00' are both tested against the RAW string, not the stripped
  // digits. Stripping destroys exactly the mark that says "this number already
  // carries its own country code" — and testing '00' against the stripped form
  // was worse than useless: '(00) 771234567' and '0-0771234567' are local
  // numbers whose punctuation happens to leave two leading zeros, and reading
  // them as an IDD prefix ate the first two digits and produced a +7
  // (Russia/Kazakhstan) number out of a Sri Lankan one.
  if (trimmed.startsWith("+")) {
    // A '+' introduces a country code, and no country code begins with a zero —
    // so '+ 0 0 771234567' is malformed, not an IDD prefix to strip. Stripping
    // it produced '771234567': a nine-digit "MSISDN" under unassigned country
    // code 77.
    return digits.startsWith("0") ? null : finalize(digits, defaultCountryCode);
  }
  if (trimmed.startsWith("00")) {
    return finalize(digits.slice(2), defaultCountryCode);
  }

  // The home country code with no '+' — the shape a copy-paste from a saved
  // contact tends to take. Length-pinned so a partial entry cannot match.
  if (digits.length === defaultCountryCode.length + HOME_NATIONAL_DIGITS &&
      digits.startsWith(defaultCountryCode)) {
    return finalize(digits, defaultCountryCode);
  }

  // National format with the trunk '0': 0771234567.
  if (
    digits.length === HOME_NATIONAL_DIGITS + 1 &&
    digits.startsWith("0") &&
    isHomeMobile(digits.slice(1))
  ) {
    return finalize(`${defaultCountryCode}${digits.slice(1)}`, defaultCountryCode);
  }

  // National significant number, trunk '0' already dropped: 771234567.
  //
  // Both this rule and the one above are pinned to the home MOBILE prefixes
  // rather than accepting any nine digits. Without that pin a foreign number
  // written in its own national format is silently re-badged as Sri Lankan, and
  // the results SMS — carrying the patient's name and their portal token —
  // goes to a stranger. See HOME_MOBILE_PREFIXES for what this does and does
  // not close.
  if (digits.length === HOME_NATIONAL_DIGITS && isHomeMobile(digits)) {
    return finalize(`${defaultCountryCode}${digits}`, defaultCountryCode);
  }

  return null;
}

/**
 * The single exit. Every rule above routes through here, so the guarantees hold
 * for all of them rather than for whichever ones someone remembered.
 *
 * Three checks, each closing a case that previously returned a WRONG number
 * instead of `null` — the failure mode that matters, because a plausible-looking
 * MSISDN gets dialled and the message names the patient:
 *
 *  1. **A trunk zero retained after the country code is dropped.**
 *     '+94 077 123 4567' is an extremely common way a Sri Lankan number is
 *     saved, and it yielded '940771234567' — twelve digits that route nowhere.
 *     One zero is stripped, then the length rule below catches anything worse.
 *  2. **A leading zero.** '+0771234567' passed the old range check untouched;
 *     no MSISDN begins with a zero.
 *  3. **A home-country number of the wrong length.** This is what rejects
 *     trailing junk ('+94 77 123 4567 ext 2' → thirteen digits) that no amount
 *     of prefix-checking catches. Foreign numbers keep only the loose E.164
 *     range, because we do not know their national plans.
 */
function finalize(msisdn: string, defaultCountryCode: string): string | null {
  let candidate = msisdn;

  if (candidate.startsWith(defaultCountryCode)) {
    const national = candidate.slice(defaultCountryCode.length);
    // Strip ONE trunk zero. Two would be a different kind of wrong, and
    // stripping until the leading digit is non-zero would happily turn
    // '9400000771234567' into something dialable.
    const withoutTrunk = national.startsWith("0") ? national.slice(1) : national;
    if (withoutTrunk.length !== HOME_NATIONAL_DIGITS) return null;
    candidate = `${defaultCountryCode}${withoutTrunk}`;
  }

  if (candidate.startsWith("0")) return null;
  return inRange(candidate) ? candidate : null;
}

function inRange(msisdn: string): boolean {
  return msisdn.length >= MIN_MSISDN_DIGITS && msisdn.length <= MAX_MSISDN_DIGITS;
}
